import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ClipboardList, Calendar, Activity, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistClientFormsPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [formsRes, clientsRes] = await Promise.all([
          axios.get(`${API}/health-tests/client/${clientId}`),
          axios.get(`${API}/users/clients`),
        ]);
        setForms(Array.isArray(formsRes.data) ? formsRes.data : []);
        const c = clientsRes.data.find(u => u.id === clientId);
        if (c) setClientName(c.name);
      } catch (err) {
        console.error('Error fetching forms:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-violet-100 border-t-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="dist-client-forms-page">
      <button onClick={() => navigate(`/distributor/clients/${clientId}`)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-profile">
        <ArrowLeft size={16} /> {clientName || 'Perfil'}
      </button>
      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Formularios Pasados</h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Tests de salud completados por {clientName}</p>
      </div>

      {forms.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-forms">
          <div className="w-20 h-20 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardList size={32} className="text-violet-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin formularios</p>
          <p className="text-sm text-gray-400 mt-1">Este cliente aun no ha completado tests de salud</p>
        </div>
      ) : (
        <div className="space-y-3">
          {forms.map((form, idx) => (
            <div
              key={form.id}
              onClick={() => navigate(`/distributor/clients/${clientId}/forms/${form.id}`)}
              className="bg-white rounded-3xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`form-card-${form.id}`}
            >
              <div className="h-1 bg-gradient-to-r from-violet-400 via-purple-500 to-indigo-500" />
              <div className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Activity size={22} className="text-violet-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm leading-tight">{form.template_name || 'Evaluacion de Salud'}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">
                        {new Date(form.completed_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-gray-300">
                  <ChevronRight size={16} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DistClientFormsPage;
